/* eslint-disable max-lines-per-function */
import { ReactElement, useMemo } from 'react';
import { compact, noop } from 'lodash';

import { PatientMedicine, PrescriptionP1HistoryDailyGroup } from '@dreryk/egabinet-prescription-frontend-api';

import { dateFormatter } from '@dreryk/react-common';
import {
   AlignItems,
   EmptyStatePlaceholder,
   JustifyContent,
   Padding,
   RowDetailsExpander,
   RowDetailsRenderer,
   StyledFlex,
   Table,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { MedicineSafetyIndicatorsConfigResolver } from '~libs.shared.medicine';

import { prescriptionsHistoryDownloadTranslationKeys } from '../translations';

import { P1PrescriptionRowDetails } from './P1PrescriptionRowDetails';
import {
   createP1PrescriptionHistoryColumnsWidth,
   p1PrescriptionHistoryDailyGroupKeyExtractor,
} from './P1PrescriptionsHistoryTable.utils';

type P1PrescriptionsHistoryTableProps = {
   readonly dailyGroups: PrescriptionP1HistoryDailyGroup[];
   readonly patientId: number;
   readonly onMedicineProcessed: VoidFunction;
};

const tableTranslationKeys = prescriptionsHistoryDownloadTranslationKeys.prescriptionsHistoryView.table;

export const P1PrescriptionsHistoryTable = (props: P1PrescriptionsHistoryTableProps): JSX.Element => {
   const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');
   const columnsWidth = createP1PrescriptionHistoryColumnsWidth(medicineDatabaseExtendedAvailable);

   const medicines = useMemo<PatientMedicine[]>(
      () =>
         props.dailyGroups.flatMap((dailyGroup) =>
            compact(
               dailyGroup.prescriptions.map((prescription) =>
                  prescription.medicine ? ({ medicine: prescription.medicine } as PatientMedicine) : undefined
               )
            )
         ),
      [props.dailyGroups]
   );

   const columns = useMemo<TableColumnConfiguration<PrescriptionP1HistoryDailyGroup>[]>(
      () =>
         compact([
            {
               name: 'medicineName',
               accessor: noop,
               label: tableTranslationKeys.label.medicineName,
               width: columnsWidth.medicineName,
               sortingDisabled: true,
               renderer: (rendererProps: ValueRendererProps<PrescriptionP1HistoryDailyGroup>) => (
                  <StyledFlex gap={'8px'} alignItems={'center'}>
                     <Text as={'strong'} testId={'issuedOn'}>
                        {dateFormatter(rendererProps.bean.issuedOn)}
                     </Text>
                  </StyledFlex>
               ),
            },
            medicineDatabaseExtendedAvailable
               ? {
                    name: 'safetyIndicators',
                    accessor: noop,
                    label: '',
                    width: columnsWidth.safetyIndicators,
                    sortingDisabled: true,
                    renderer: () => null,
                 }
               : undefined,
            {
               name: 'package',
               accessor: noop,
               label: tableTranslationKeys.label.package,
               width: columnsWidth.package,
               sortingDisabled: true,
               renderer: () => null,
            },
            {
               name: 'quantity',
               accessor: noop,
               label: tableTranslationKeys.label.quantity,
               width: columnsWidth.quantity,
               sortingDisabled: true,
               renderer: () => null,
            },
            {
               name: 'issuedFrom',
               accessor: noop,
               label: tableTranslationKeys.label.issuedFrom,
               width: columnsWidth.issuedFrom,
               alignItems: AlignItems.CENTER,
               padding: Padding.NONE,
               sortingDisabled: true,
               renderer: () => null,
            },
            {
               name: 'expander',
               accessor: noop,
               label: '',
               width: columnsWidth.expander,
               sortingDisabled: true,
               justifyContent: JustifyContent.END,
               renderer: (rendererProps: ValueRendererProps<PrescriptionP1HistoryDailyGroup>): ReactElement => (
                  <RowDetailsExpander {...rendererProps} testId={'expander'} />
               ),
            },
         ]),
      [columnsWidth, medicineDatabaseExtendedAvailable]
   );

   const rowDetailsRenderer = useMemo<RowDetailsRenderer<PrescriptionP1HistoryDailyGroup>>(
      () => ({ bean }) =>
         bean.prescriptions.map((prescription, index) => (
            <P1PrescriptionRowDetails
               // eslint-disable-next-line react/no-array-index-key
               key={`${String(bean.issuedOn)}-${index}`}
               prescription={prescription}
               columnsWidth={columnsWidth}
               patientId={props.patientId}
               onMedicineProcessed={props.onMedicineProcessed}
               safetyIndicatorsVisible={medicineDatabaseExtendedAvailable}
               testId={`prescription-${String(bean.issuedOn)}-${index}`}
            />
         )),
      [columnsWidth, props.patientId, props.onMedicineProcessed, medicineDatabaseExtendedAvailable]
   );

   const defaultRowDetailsOpen = useMemo(
      () =>
         props.dailyGroups.reduce<Record<string, boolean>>((acc, dailyGroup) => {
            acc[String(dailyGroup.issuedOn)] = true;
            return acc;
         }, {}),
      [props.dailyGroups]
   );

   const dataConfiguration = useMemo(() => ({ data: props.dailyGroups }), [props.dailyGroups]);

   return (
      <>
         {medicineDatabaseExtendedAvailable ? <MedicineSafetyIndicatorsConfigResolver medicines={medicines} /> : null}
         <Table<PrescriptionP1HistoryDailyGroup>
            testId={'p1PrescriptionsHistoryTable'}
            columns={columns}
            dataConfiguration={dataConfiguration}
            keyExtractor={p1PrescriptionHistoryDailyGroupKeyExtractor}
            rowDetailsRenderer={rowDetailsRenderer}
            defaultRowDetailsOpen={defaultRowDetailsOpen}
            compact
            placeholder={
               <EmptyStatePlaceholder
                  heading={prescriptionsHistoryDownloadTranslationKeys.prescriptionsHistoryView.noMedicinesIssued}
                  testId={'noMedicinesIssued'}
               />
            }
         />
      </>
   );
};
